// Shared types

import { TPrimaryDetails } from '../users/users.interfaces';
import { TActions } from './shared.interfaces';

export enum ATTENDANCE_STATUS {
  PRESENT = 'present', // P
  ABSENT = 'absent', // A
  LATE = 'late', // L
  LATE_WITH_EXCUSE = 'lateWithExcuse', // E
  HALF_DAY = 'halfDay', // F
  HOLIDAY = 'holiday', // H
  SKIPPED = 'skipped', // For users who start late or attend school-related events
}

// Attendance summary metrics
export interface IAttendanceSummaryMetrics {
  totalPresent: number;
  totalLate: number;
  totalAbsent: number;
  totalHalfDay: number;
  totalHoliday: number;
  totalLateWithExcuse?: number;
  attendanceRate?: number; // Percentage (0-100)
  tardinessRate?: number; // Percentage (0-100)
  excusedAbsentRate?: number; // Percentage (0-100)
  unexcusedAbsentRate?: number; // Percentage (0-100)
}

// Base attendance record used in date-based structure (group perspective)
export type TAttendanceByDateRecord = {
  attendanceId: string;
  userDetails: TPrimaryDetails;
  attendanceStatus: ATTENDANCE_STATUS;
  comment?: string;
  actions: TActions;
};

// Base attendance record used in user-based structure (user perspective)
export type TAttendanceByUserRecord = {
  attendanceId: string;
  date: string;
  attendanceStatus: ATTENDANCE_STATUS;
  comment?: string;
  actions: TActions;
};

// Group/Date-based attendance entry with summary metrics
export interface IAttendanceByDateEntry {
  date: string;
  records: TAttendanceByDateRecord[];
  summary?: IAttendanceSummaryMetrics;
}

// Date-based response: date is the primary reference, contains list of users with attendance details
// Supports both object format (legacy) and array format (new)
export type TAttendanceByDateResponse =
  | { [date: string]: TAttendanceByDateRecord[] }
  | IAttendanceByDateEntry[];

/** Paginated wire DTO from GET .../attendance/by-date */
export interface IAttendanceByDatePageWireDto {
  data: IAttendanceByDateEntry[];
  total: number;
  page: number;
  size: number;
}

/** Unwrap paginated API body to the shape expected by date-based attendance UI */
export function normalizeAttendanceByDateResponseBody(
  body: unknown
): TAttendanceByDateResponse {
  if (
    body !== null &&
    typeof body === 'object' &&
    !Array.isArray(body) &&
    Array.isArray((body as Partial<IAttendanceByDatePageWireDto>).data) &&
    typeof (body as Partial<IAttendanceByDatePageWireDto>).total === 'number' &&
    typeof (body as Partial<IAttendanceByDatePageWireDto>).page === 'number' &&
    typeof (body as Partial<IAttendanceByDatePageWireDto>).size === 'number'
  ) {
    return (body as IAttendanceByDatePageWireDto).data;
  }
  return body as TAttendanceByDateResponse;
}

// User-based attendance entry with summary metrics
export interface IAttendanceByUser {
  userDetails: TPrimaryDetails;
  attendanceRecords: TAttendanceByUserRecord[];
  summary?: IAttendanceSummaryMetrics;
}

// User-based response: userId is the primary reference, contains list of dates with attendance details
export type TAttendanceByUserResponse = {
  [userId: string]: IAttendanceByUser;
};

// Course/Subject-based attendance breakdown (for user perspective)
export interface IAttendanceByCourse {
  courseId: string;
  courseName: string;
  attendanceRate: number;
  present: number;
  late: number;
  excusedAbsent: number;
  unexcusedAbsent: number;
  records: TAttendanceByUserRecord[];
}

// Extended user-based attendance with course breakdown
export interface IAttendanceByUserExtended extends IAttendanceByUser {
  courses?: IAttendanceByCourse[];
}

// Request payload for creating attendance records (POST /students/attendance)
export interface ICreateAttendanceRequest {
  date: string;
  records: IAttendanceRecordInput[];
  createdBy: string;
}

export interface IAttendanceRecordInput {
  studentId: string;
  attendanceStatus: ATTENDANCE_STATUS;
  comment?: string;
}
